/**
 * 离线写入队列（前端断网兜底）。
 *
 * 解决 P8：用户断网 / 后端 5xx 时，关键写入（saveProjectDraft / saveStoryboardPrompt /
 *   saveStoryboardVideoConfig / saveProjectEntity 等）会丢失，用户辛苦改的内容白白浪费。
 * 设计：
 *   - 把失败的写请求序列化到 IDB（fallback localStorage），按 nextRetryAt 排队
 *   - 浏览器 online 事件 / setInterval(15s) 触发 drain：逐条重发，幂等成功即删除
 *   - 同一 key（path+body hash）只保留最新一条，避免用户连改多次留下大量过期 enqueue
 *   - 单条最多尝试 MAX_ATTEMPTS 次；超时即丢弃并在 UI 上给出警告
 *   - 仅覆盖我们主动 enqueue 的 path（白名单），避免无差别代理 fetch
 *
 * 用法：
 *   await enqueue(path, body);           // 失败时由 fetchWithQueue 内部自动调用
 *   await drainOnce();                   // 立即试一次（手动触发）
 *   startBackgroundDrainer();            // 挂上 online + setInterval 监听
 *   stopBackgroundDrainer();             // 退出登录 / 页 unmount 时调用
 *   getPendingCount();                   // 队列长度（用于 UI 显示）
 */

import { kvStore } from './kvStore';

const QUEUE_DB_KEY = 'offline_write_queue:v1';
const MAX_ATTEMPTS = 10;
const IDEMPOTENT_PATHS = new Set([
  'saveProjectDraft',
  'saveStoryboardPrompt',
  'saveStoryboardVideoConfig',
  'saveProjectEntity',
  'updateProjectMetaJson'
  // 不加入 createXxx / deleteXxx / pay 等副作用强接口
]);

function hashBody(body) {
  try {
    return JSON.stringify(body || null);
  } catch (e) {
    return String(Date.now());
  }
}

function entryKey(path, body) {
  return path + '#' + hashBody(body);
}

async function readQueue() {
  const v = await kvStore.get(QUEUE_DB_KEY, () => {
    try { return localStorage.getItem(QUEUE_DB_KEY); } catch (e) { return null; }
  });
  return Array.isArray(v) ? v : [];
}

async function writeQueue(list) {
  return kvStore.set(QUEUE_DB_KEY, list, (serialized) => {
    try { localStorage.setItem(QUEUE_DB_KEY, serialized); } catch (e) { /* ignore */ }
  });
}

/**
 * 把一次失败的关键写入入队。
 *  - path 必须属于 IDEMPOTENT_PATHS 白名单
 *  - 同一 path+body 已存在时合并 attempts、保留最早 createdAt
 *  - 返回新的 entries 长度
 */
export async function enqueue(path, body, errMsg) {
  if (!IDEMPOTENT_PATHS.has(path)) {
    return -1;
  }
  const k = entryKey(path, body);
  const list = await readQueue();
  const now = Date.now();
  const idx = list.findIndex(it => it && it.key === k);
  const next = {
    key: k,
    path,
    body,
    attempts: idx >= 0 ? (list[idx].attempts || 0) + 1 : 1,
    createdAt: idx >= 0 ? (list[idx].createdAt || now) : now,
    nextRetryAt: now,
    lastErr: String(errMsg || '').slice(0, 240)
  };
  if (idx >= 0) {
    list[idx] = next;
  } else {
    list.push(next);
  }
  await writeQueue(list);
  return list.length;
}

/**
 * 直接移除某条（drain 成功后调用）。
 */
export async function removeFromQueue(path, body) {
  const k = entryKey(path, body);
  const list = await readQueue();
  const next = list.filter(it => it && it.key !== k);
  if (next.length !== list.length) {
    await writeQueue(next);
  }
  return list.length - next.length;
}

async function trySend(entry, request) {
  try {
    await request(entry.path, entry.body);
    return { ok: true };
  } catch (e) {
    return { ok: false, err: (e && e.message) ? String(e.message) : '请求失败' };
  }
}

/**
 * 主动尝试一次（一般由 online 事件或 setInterval 触发）
 *  - 只重发 nextRetryAt <= now 的 entries
 *  - 每条之间 sleep 250ms，避免刚恢复的网络被瞬时打爆
 *  - 任何一条都更新 attempts / nextRetryAt
 *
 * @param {(path:string,body:any)=>Promise<any>} requestFn 实际发起请求的函数（默认复用 userApi 但避免循环 import）
 * @returns {{sent:number, okCount:number, failCount:number, droppedCount:number}}
 */
export async function drainOnce(requestFn) {
  if (typeof requestFn !== 'function') {
    return { sent: 0, okCount: 0, failCount: 0, droppedCount: 0 };
  }
  const list = await readQueue();
  const now = Date.now();
  let okCount = 0;
  let failCount = 0;
  let droppedCount = 0;
  const survivors = [];
  for (const it of list) {
    if (!it || !it.path) continue;
    if ((it.nextRetryAt || 0) > now) {
      survivors.push(it);
      continue;
    }
    if ((it.attempts || 0) >= MAX_ATTEMPTS) {
      // 超过最大尝试次数 → 丢弃
      droppedCount++;
      continue;
    }
    // eslint-disable-next-line no-await-in-loop
    const r = await trySend(it, requestFn);
    if (r.ok) {
      okCount++;
      // 成功 → 不再保留
      continue;
    }
    it.attempts = (it.attempts || 0) + 1;
    it.lastErr = r.err;
    it.nextRetryAt = now + Math.min(60_000, 1000 * Math.pow(2, Math.min(it.attempts, 6)));
    failCount++;
    survivors.push(it);
  }
  await writeQueue(survivors);
  return { sent: list.length, okCount, failCount, droppedCount };
}

/**
 * 启动后台 drainer：online 事件 + 15s tick 兜底
 *  - 同一进程内只会启动一次
 *  - 返回用于在 unmount 时清理的 stop 函数
 */
let _started = false;
let _timer = null;
let _onlineHandler = null;
let _requestFn = null;

export function startBackgroundDrainer(requestFn) {
  if (_started) return stopBackgroundDrainer;
  if (typeof requestFn !== 'function') {
    return () => { /* noop */ };
  }
  _requestFn = requestFn;
  _timer = setInterval(() => { drainOnce(_requestFn).catch(() => {}); }, 15_000);
  if (typeof window !== 'undefined' && window.addEventListener) {
    _onlineHandler = () => {
      // 立即尝试一次；online 事件触发表示刚恢复，避免延迟到下一个 15s tick
      drainOnce(_requestFn).catch(() => {});
    };
    window.addEventListener('online', _onlineHandler);
  }
  _started = true;
  return stopBackgroundDrainer;
}

export function stopBackgroundDrainer() {
  if (_timer) {
    clearInterval(_timer);
    _timer = null;
  }
  if (_onlineHandler && typeof window !== 'undefined' && window.removeEventListener) {
    try { window.removeEventListener('online', _onlineHandler); } catch (e) { /* ignore */ }
    _onlineHandler = null;
  }
  _started = false;
  _requestFn = null;
}

/**
 * 队列统计（仅 length）。可在 UI 显示「离线兜底:N 条待重发」。
 */
export async function getPendingCount() {
  const list = await readQueue();
  return list.length;
}

/**
 * 显式丢弃全部队列（用户主动放弃 / 退出登录场景）。
 */
export async function clearQueue() {
  await writeQueue([]);
}

/**
 * 内部测试 helper：导出白名单便于断言。
 */
export const _IDEMPOTENT_PATHS = IDEMPOTENT_PATHS;
export const _MAX_ATTEMPTS = MAX_ATTEMPTS;
